import { DOOR_PULL_KEY_ENROLLMENT_OUTCOMES, DOOR_PULL_REPORT_ERRORS } from "./door-pull.js";
import { doorPullDataPlaneIdSchema, doorPullKidSchema, doorPullPublicJwkSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/door-pull.schemas.ts
/**
* Zod schemas for the per-DP door identity wire (2026-10-15-per-dp-door-identity, D2, D8, D9, D10).
* The types, the route and the freshness rule are in `door-pull.ts`.
*
* No schema uses `.transform()`, so each one stays representable in the JSON Schema that
* `createOpenApiDocument()` writes, where release-engine binds the enrollment body with
* `@Body({ schema })`. Each object is strict, and `StatusReportSchema` is strict end to end, so an
* unknown key in `doorPull` fails the whole report.
*/
/**
* The body of `POST /v1/operator/data-planes/:dpId/door-pull-key`: the public half of the data
* plane's KMS door key, as a P-256 JWK of exactly `kty`, `crv`, `x` and `y` (F3). The body names no
* data plane: release-engine takes the data plane from the client certificate. Strict, so any other
* key, a `kid` included, is a `ZodError`.
*/
const DoorPullKeyEnrollmentRequestSchema = z.object({ publicJwk: doorPullPublicJwkSchema.describe("The public half of the data plane KMS door key") }).strict();
/**
* The answer of the enrollment route.
*
* - `dataPlaneId`: the data plane the client certificate names, a lower-case UUID.
* - `kid`: the RFC 7638 SHA-256 thumbprint of the enrolled key (`doorPullKeyThumbprint`), which the
*   operator program puts in the header of every assertion it signs.
* - `outcome`: `enrolled` when release-engine stored the key, `unchanged` when it held it already.
*
* Strict.
*/
const DoorPullKeyEnrollmentResponseSchema = z.object({
	dataPlaneId: doorPullDataPlaneIdSchema,
	kid: doorPullKidSchema,
	outcome: z.enum(DOOR_PULL_KEY_ENROLLMENT_OUTCOMES)
}).strict();
/**
* The `doorPull` member of a status report: what the operator program's door-pull refresher holds
* (D8). The operator sends it on every report, full or delta, once its refresher runs; it is a header
* member (`STATUS_REPORT_HEADER_MEMBERS`), outside every hash. release-engine stores it on
* `release.dp_live_status` for the reporting data plane only. provisioning-svc reads it and calls
* `isDoorPullSelfMintFresh`.
*
* - `kid`: the key id of the door key the operator program signs with, or `null` before it read the
*   public key from KMS.
* - `tokenExpiresAt`: the expiry of the newest token the operator program minted for itself, an ISO
*   8601 UTC timestamp, or `null` when it holds none. The token itself never travels (F9).
* - `lastExchangeAt`: when its last exchange succeeded, or `null` when none has.
* - `lastError`: the reason of its most recent failed attempt after the last success
*   (`DOOR_PULL_REPORT_ERRORS`), or `null` when the last attempt succeeded or none ran.
* - `namespacesWritten`: how many namespaces of its list hold the newest token, 0 to
*   `DOOR_PULL_NAMESPACES_MAX`.
* - `namespacesTotal`: how many namespaces its list holds, 0 to `DOOR_PULL_NAMESPACES_MAX`.
*
* Refused, with a path to the member: `namespacesWritten` above `namespacesTotal`; a
* `tokenExpiresAt` with a `null` `kid` or a `null` `lastExchangeAt`, because a self-minted token
* comes from a signed exchange. Any other key, a fractional or negative count, a count above the
* maximum, an unknown error or a malformed timestamp is a `ZodError` too.
*
* Absent from a report means the operator predates the member or its refresher is off; the CP then
* keeps minting for that data plane (D8). CP first, as for `appPools`: the CP release that parses
* this member ships before the operator release that sends it, because an un-upgraded CP refuses
* the whole strict report.
*/
const DoorPullReportSchema = z.object({
	kid: doorPullKidSchema.nullable(),
	tokenExpiresAt: z.iso.datetime().nullable(),
	lastExchangeAt: z.iso.datetime().nullable(),
	lastError: z.enum(DOOR_PULL_REPORT_ERRORS).nullable(),
	namespacesWritten: z.number().int().min(0).max(64),
	namespacesTotal: z.number().int().min(0).max(64)
}).strict().superRefine((report, ctx) => {
	if (report.namespacesWritten > report.namespacesTotal) ctx.addIssue({
		code: "custom",
		path: ["namespacesWritten"],
		message: "at most namespacesTotal"
	});
	if (report.tokenExpiresAt !== null && (report.kid === null || report.lastExchangeAt === null)) ctx.addIssue({
		code: "custom",
		path: ["tokenExpiresAt"],
		message: "a self-minted token needs a kid and a successful exchange"
	});
});
//#endregion
export { DoorPullKeyEnrollmentRequestSchema, DoorPullKeyEnrollmentResponseSchema, DoorPullReportSchema };

//# sourceMappingURL=door-pull.schemas.js.map